/**
 * In-app password reset. Requires the Auth email template action URL to point at this host
 * (e.g. {origin}/ResetPassword or {origin}/__/auth/action — see FirebaseAuthActionRedirect).
 * Reads oobCode from the query string or, if needed, from the URL hash.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../firebase";
import ErrorBanner from "../components/ErrorBanner";
import { LoginWithMarketingLayout } from "../components/LoginMarketingAside";
import PasswordInput from "../components/PasswordInput";
import useAccountBranding from "../shared/hooks/useAccountBranding";
import "./Login.css";

/** Same minimum as event self-registration (EventCalendar signup). */
const MIN_PASSWORD_LENGTH = 6;

function getOobCodeFromLocation(location) {
  const fromSearch = new URLSearchParams(location.search || "");
  let code = (fromSearch.get("oobCode") || "").trim();
  if (code) return code;
  const raw = (location.hash || "").replace(/^#/, "");
  if (!raw) return "";
  const queryPart = raw.includes("?") ? raw.split("?").slice(1).join("?") : raw;
  const fromHash = new URLSearchParams(queryPart);
  return (fromHash.get("oobCode") || "").trim();
}

function isInvitePasswordSetupFlow(location) {
  const fromSearch = new URLSearchParams(location.search || "");
  return (fromSearch.get("source") || "").trim() === "invite";
}

function getInviteFormLead(accountFullName) {
  const name = (accountFullName || "").trim();
  if (name) {
    return `You have been invited to create an account with ${name} registration system. Choose a password below to continue.`;
  }
  return "You have been invited. Choose a password below to continue.";
}

/** User-facing copy: invite (set password) vs forgot-password reset. */
function getResetPasswordCopy(inviteFlow, accountFullName) {
  if (inviteFlow) {
    return {
      checkingTitle: "Set your password",
      checkingLead: "Verifying your invite link…",
      checkingMuted: "Verifying invite link…",
      invalidTitle: "Invite link no longer valid",
      invalidLead:
        "This invite link is missing, has already been used, or has expired.",
      invalidHelp:
        "If you already set your password, sign in below. Otherwise ask your administrator to resend the invite.",
      formTitle: "Set your password",
      formLead: getInviteFormLead(accountFullName),
      passwordLabel: "Password",
      confirmLabel: "Confirm password",
      passwordPlaceholder: "Password",
      confirmPlaceholder: "Confirm password",
      submit: "Set password",
      submitting: "Setting password…",
      successTitle: "Password set",
      successLead: "You can sign in with your new password.",
      successMessage: "Your password has been set successfully.",
      expiredActionError:
        "This invite link is invalid or has expired. Ask your administrator to resend the invite.",
      submitError: "Could not set password.",
    };
  }
  return {
    checkingTitle: "Reset password",
    checkingLead: "Verifying your link…",
    checkingMuted: "Verifying reset link…",
    invalidTitle: "Reset link invalid",
    invalidLead: "This password reset link is missing, invalid, or has expired.",
    invalidHelp: "Request a new reset link to get a fresh email.",
    formTitle: "Reset your password",
    formLead: "Create a new, strong password you haven't used before.",
    passwordLabel: "New Password",
    confirmLabel: "Confirm New Password",
    passwordPlaceholder: "New password",
    confirmPlaceholder: "Confirm new password",
    submit: "Reset Password",
    submitting: "Updating…",
    successTitle: "Password updated",
    successLead: "You can sign in with your new password.",
    successMessage: "Your password has been reset successfully.",
    expiredActionError: "This reset link is invalid or has expired. Request a new one from Sign in.",
    submitError: "Could not reset password.",
  };
}

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const oobCode = useMemo(() => getOobCodeFromLocation(location), [location.search, location.hash]);
  const { accountBranding } = useAccountBranding();
  const inviteFlow = useMemo(() => isInvitePasswordSetupFlow(location), [location.search]);
  const copy = useMemo(
    () => getResetPasswordCopy(inviteFlow, accountBranding?.full_name),
    [inviteFlow, accountBranding?.full_name]
  );

  const [codeStatus, setCodeStatus] = useState("checking"); // "checking" | "valid" | "invalid"
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [done, setDone] = useState(false);

  const verify = useCallback(async () => {
    if (!oobCode) {
      setCodeStatus("invalid");
      return;
    }
    try {
      await verifyPasswordResetCode(auth, oobCode);
      setCodeStatus("valid");
    } catch {
      setCodeStatus("invalid");
    }
  }, [oobCode]);

  useEffect(() => {
    verify();
  }, [verify]);

  const trimmedNew = newPassword;
  const trimmedConfirm = confirmPassword;
  const mismatch =
    trimmedConfirm.length > 0 && trimmedNew !== trimmedConfirm;
  const tooShort = trimmedNew.length > 0 && trimmedNew.length < MIN_PASSWORD_LENGTH;
  const canSubmit =
    codeStatus === "valid" &&
    trimmedNew.length >= MIN_PASSWORD_LENGTH &&
    trimmedNew === trimmedConfirm &&
    !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    if (trimmedNew !== trimmedConfirm) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    if (trimmedNew.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      await confirmPasswordReset(auth, oobCode, trimmedNew);
      setDone(true);
    } catch (err) {
      const code = err.code || "";
      if (code === "auth/weak-password") {
        setErrorMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      } else if (code === "auth/expired-action-code" || code === "auth/invalid-action-code") {
        setErrorMessage(copy.expiredActionError);
        setCodeStatus("invalid");
      } else {
        setErrorMessage(err.message || copy.submitError);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (codeStatus === "checking") {
    return (
      <LoginWithMarketingLayout>
        <header className="login-hero">
          <h2>{copy.checkingTitle}</h2>
          <p>{copy.checkingLead}</p>
        </header>
        <div className="login-card">
          <p className="auth-muted">{copy.checkingMuted}</p>
        </div>
      </LoginWithMarketingLayout>
    );
  }

  if (codeStatus === "invalid" || !oobCode) {
    return (
      <LoginWithMarketingLayout>
        <header className="login-hero">
          <h2>{copy.invalidTitle}</h2>
          <p>{copy.invalidLead}</p>
        </header>
        <div className="login-card">
          {errorMessage ? (
            <ErrorBanner message={errorMessage} onDismiss={() => setErrorMessage("")} />
          ) : (
            <p className="auth-muted">{copy.invalidHelp}</p>
          )}
          <div className="auth-text-links">
            {!inviteFlow ? (
              <Link className="auth-text-link" to="/ForgotPassword">
                Request a new reset link
              </Link>
            ) : null}
            <Link className="auth-text-link" to="/Login">
              Back to Sign In
            </Link>
          </div>
        </div>
      </LoginWithMarketingLayout>
    );
  }

  if (done) {
    return (
      <LoginWithMarketingLayout>
        <header className="login-hero">
          <h2>{copy.successTitle}</h2>
          <p>{copy.successLead}</p>
        </header>
        <div className="login-card">
          <p className="login-message success">{copy.successMessage}</p>
          <button type="button" className="auth-button-full" onClick={() => navigate("/Login")}>
            Sign in
          </button>
        </div>
      </LoginWithMarketingLayout>
    );
  }

  return (
    <LoginWithMarketingLayout>
      <header className="login-hero">
        <h2>{copy.formTitle}</h2>
        <p>{copy.formLead}</p>
      </header>
      <div className="login-card">
        <form onSubmit={handleSubmit}>
          <div className="password-requirements" aria-live="polite">
            <div className="password-requirements-title">Password requirements</div>
            <ul>
              <li>At least {MIN_PASSWORD_LENGTH} characters (8 or more recommended for a strong password).</li>
            </ul>
          </div>
          <label htmlFor="reset-new-password">{copy.passwordLabel}</label>
          <PasswordInput
            id="reset-new-password"
            value={newPassword}
            placeholder={copy.passwordPlaceholder}
            autoComplete="new-password"
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <label htmlFor="reset-confirm-password">{copy.confirmLabel}</label>
          <PasswordInput
            id="reset-confirm-password"
            value={confirmPassword}
            placeholder={copy.confirmPlaceholder}
            autoComplete="new-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {mismatch && (
            <p className="login-message error" role="alert">
              Passwords do not match.
            </p>
          )}
          {tooShort && !mismatch && (
            <p className="login-message error" role="alert">
              Password must be at least {MIN_PASSWORD_LENGTH} characters.
            </p>
          )}
          <button type="submit" className="auth-button-full" disabled={!canSubmit}>
            {submitting ? copy.submitting : copy.submit}
          </button>
          {errorMessage && <ErrorBanner message={errorMessage} onDismiss={() => setErrorMessage("")} />}
          <Link className="auth-text-link" to="/Login">
            Back to Sign In
          </Link>
        </form>
      </div>
    </LoginWithMarketingLayout>
  );
}

export default ResetPassword;
